/**
 * Signs a bank account in the state the signer sees. Before the second signature it shows who gave
 * the first one, or the system's evidence when the system check gave it.
 */
import { useState } from 'react';
import { Button, ConfirmationModal, Loader, Modal } from '@insio/ui-toolkit';
import { BankAccountVerificationState } from '@insio/shared-types';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useFormatTimestamp } from '@/hooks/use-date-formatters';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import { useDelayedLoading } from '@/hooks/use-delayed-loading';
import { useBankAccountMutations } from '../hooks/use-bank-account-mutations';
import { latestSystemEvidence, useBankAccountEvents } from '../hooks/use-bank-account-events';
import type { PartyBankAccount } from '../hooks/use-party-bank-accounts';
import {
  BankAccountError,
  bankAccountError,
  isStaleViewRefusal
} from '../utils/bank-account-error';
import { BankAccountSummary } from './BankAccountSummary';
import { SystemCheckEvidenceDetails } from './SystemCheckEvidenceDetails';

interface SignBankAccountModalProps {
  account: PartyBankAccount;
  /** The partner's name, compared with the name the VAT register gives. */
  partyName: string | null;
  onClose: () => void;
}

export function SignBankAccountModal({ account, partyName, onClose }: SignBankAccountModalProps) {
  const t = useFeatureTranslation('partyBankAccounts');
  const formatTimestamp = useFormatTimestamp();
  const { showSuccess, showWarning } = useToastHelpers();
  const { sign, refresh } = useBankAccountMutations();
  const [error, setError] = useState<BankAccountError | null>(null);
  const [saving, setSaving] = useState(false);

  const isSecond = account.verificationState === BankAccountVerificationState.PENDING;
  const needsEvidence = isSecond && account.isFirstSignatureSystem;
  const evidenceQuery = useBankAccountEvents(account.id, { skip: !needsEvidence });
  const evidence = latestSystemEvidence(evidenceQuery.events);
  const showEvidenceLoader = useDelayedLoading(needsEvidence && evidenceQuery.loading);

  const handleConfirm = async () => {
    setSaving(true);
    setError(null);
    try {
      await sign({ id: account.id, expectedState: account.verificationState });
      showSuccess(isSecond ? t('toast.verified') : t('toast.signedFirst'));
      onClose();
    } catch (err) {
      const refusal = bankAccountError(err);
      // What the signer saw may be gone: the list shows the account as it is now (best effort).
      if (isStaleViewRefusal(refusal)) await refresh();
      if (refusal === BankAccountError.STATE_CHANGED) {
        showWarning(t('errors.stateChanged'));
        onClose();
        return;
      }
      setError(refusal);
    } finally {
      setSaving(false);
    }
  };

  const firstSignedAt = account.firstSignedAt ? formatTimestamp(account.firstSignedAt) : '';
  const firstSigner = account.isFirstSignatureSystem
    ? t('sign.systemSigned', { date: firstSignedAt })
    : account.firstSignedBy
      ? t('sign.firstSignedBy', { name: account.firstSignedBy.displayName, date: firstSignedAt })
      : t('sign.firstSignerGone', { date: firstSignedAt });

  let evidenceContent = null;
  if (needsEvidence) {
    if (evidenceQuery.loading) evidenceContent = showEvidenceLoader ? <Loader size='sm' /> : null;
    else if (evidence) {
      evidenceContent = <SystemCheckEvidenceDetails evidence={evidence} partyName={partyName} />;
    } else if (evidenceQuery.error) {
      evidenceContent = (
        <div
          className='flex flex-wrap items-center gap-sm'
          data-testid='bank-account-evidence-error'
        >
          <p className='text-sm text-error-text'>{t('sign.evidenceLoadError')}</p>
          <Button
            variant='default'
            outline='default'
            size='sm'
            onClick={() => void evidenceQuery.retry()}
          >
            {t('sign.evidenceRetry')}
          </Button>
        </div>
      );
    } else {
      evidenceContent = (
        <p className='text-sm text-foreground-muted' data-testid='bank-account-evidence-missing'>
          {t('sign.evidenceMissing')}
        </p>
      );
    }
  }

  return (
    <ConfirmationModal
      open
      testId='sign-bank-account-modal'
      variant='primary'
      title={isSecond ? t('sign.secondTitle') : t('sign.firstTitle')}
      message={isSecond ? t('sign.secondMessage') : t('sign.firstMessage')}
      confirmLabel={t('sign.confirm')}
      cancelLabel={t('sign.cancel')}
      loading={saving}
      // A second signature over the system check needs its evidence in view.
      confirmDisabled={needsEvidence && !evidence}
      onConfirm={() => void handleConfirm()}
      onClose={onClose}
      body={
        <div className='flex flex-col gap-md'>
          <BankAccountSummary account={account} />
          {isSecond && (
            <div className='flex flex-col gap-xs' data-testid='bank-account-first-signature'>
              <p className='text-2xs font-semibold uppercase tracking-wide text-foreground-muted'>
                {t('sign.firstSignature')}
              </p>
              <p className='text-sm text-foreground-base'>{firstSigner}</p>
              {evidenceContent}
            </div>
          )}
          {error && <Modal.Error role='alert'>{t(`errors.${error}`)}</Modal.Error>}
        </div>
      }
    />
  );
}
